import { useState, useEffect } from 'react'
import { api } from '@/services/api-client'
import { Shield, Users, BarChart3, Tag, CreditCard, Percent, Key, Trash2, Eye, EyeOff, Search, ChevronDown, ChevronRight, Plus, X, Check, Settings } from 'lucide-react'

type AdminTab = 'stats' | 'users' | 'promo' | 'plans' | 'commission' | 'listings'

export function AdminPanel() {
  const [tab, setTab] = useState<AdminTab>('stats')
  const [authorized, setAuthorized] = useState<boolean | null>(null)

  useEffect(() => {
    api.adminCheck().then(r => setAuthorized(r.is_super_admin)).catch(() => setAuthorized(false))
  }, [])

  if (authorized === null) return <div className="admin"><p>Sprawdzanie uprawnień...</p></div>
  if (!authorized) return <div className="admin"><h1><Shield size={24} /> Brak dostępu</h1><p>Panel administracyjny jest dostępny tylko dla super administratora.</p></div>

  const tabs: { id: AdminTab; label: string; icon: any }[] = [
    { id: 'stats', label: 'Statystyki', icon: BarChart3 },
    { id: 'users', label: 'Użytkownicy', icon: Users },
    { id: 'listings', label: 'Oferty API', icon: Key },
    { id: 'promo', label: 'Kody promocyjne', icon: Tag },
    { id: 'plans', label: 'Plany subskrypcji', icon: CreditCard },
    { id: 'commission', label: 'Prowizje', icon: Percent },
  ]

  return (
    <div className="admin">
      <h1 className="admin-title"><Shield size={24} /> Panel Administratora</h1>
      <div className="admin-tabs">
        {tabs.map(t => (
          <button key={t.id} className={`admin-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>
      <div className="admin-content">
        {tab === 'stats' && <StatsTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'listings' && <ListingsTab />}
        {tab === 'promo' && <PromoTab />}
        {tab === 'plans' && <PlansTab />}
        {tab === 'commission' && <CommissionTab />}
      </div>
    </div>
  )
}

function StatsTab() {
  const [stats, setStats] = useState<any>(null)
  useEffect(() => { api.adminGetStats().then(setStats).catch(console.error) }, [])

  if (!stats) return <p>Ładowanie statystyk...</p>

  const formatUSD = (dollars: number) => `$${Number(dollars).toFixed(2)}`

  return (
    <div className="admin-stats">
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <span className="admin-stat-value">{stats.users}</span>
          <span className="admin-stat-label">Użytkownicy</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{stats.projects}</span>
          <span className="admin-stat-label">Projekty</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{stats.threads}</span>
          <span className="admin-stat-label">Wątki</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{stats.messages}</span>
          <span className="admin-stat-label">Wiadomości</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{stats.active_listings}</span>
          <span className="admin-stat-label">Aktywne oferty</span>
        </div>
        <div className="admin-stat-card admin-stat-card--highlight">
          <span className="admin-stat-value">{formatUSD(stats.total_wallet_balance)}</span>
          <span className="admin-stat-label">Saldo portfeli</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{formatUSD(stats.total_deposited)}</span>
          <span className="admin-stat-label">Łącznie wpłacono</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{formatUSD(stats.total_spent)}</span>
          <span className="admin-stat-label">Łącznie wydano</span>
        </div>
      </div>

      {stats.listings_by_provider?.length > 0 && (
        <div className="admin-section">
          <h3>Oferty wg dostawcy</h3>
          <div className="admin-mini-table">
            {stats.listings_by_provider.map((lp: any) => (
              <div key={lp.provider_type} className="admin-mini-row">
                <span>{lp.provider_type}</span>
                <span className="admin-badge">{lp.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.recent_signups?.length > 0 && (
        <div className="admin-section">
          <h3>Ostatnie rejestracje</h3>
          <div className="admin-mini-table">
            {stats.recent_signups.map((u: any) => (
              <div key={u.id} className="admin-mini-row">
                <span>{u.display_name} ({u.email})</span>
                <span className="admin-badge">{u.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function UsersTab() {
  const [users, setUsers] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [expandedUser, setExpandedUser] = useState<string | null>(null)
  const [userKeys, setUserKeys] = useState<Record<string, any[]>>({})
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({})

  const fetchUsers = (q?: string) => {
    api.adminGetUsers(q || search).then(r => { setUsers(r.users); setTotal(r.total) }).catch(console.error)
  }

  useEffect(() => { fetchUsers('') }, [])

  const handleSearch = () => fetchUsers()

  const toggleUserExpand = async (userId: string) => {
    if (expandedUser === userId) { setExpandedUser(null); return }
    setExpandedUser(userId)
    if (!userKeys[userId]) {
      const keys = await api.adminGetUserKeys(userId)
      setUserKeys(prev => ({ ...prev, [userId]: keys }))
    }
  }

  const toggleKeyVisibility = (keyId: string) => {
    setShowKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }))
  }

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!confirm(`Usunąć użytkownika ${email}? Ta operacja jest nieodwracalna.`)) return
    await api.adminDeleteUser(userId)
    fetchUsers()
  }

  const handleRoleChange = async (userId: string, newRole: string) => {
    await api.adminUpdateUser(userId, { role: newRole })
    fetchUsers()
  }

  const formatUSD = (dollars: number | null) => dollars != null ? `$${Number(dollars).toFixed(2)}` : '$0.00'

  return (
    <div className="admin-users">
      <div className="admin-search-bar">
        <div className="admin-search-input-wrap">
          <Search size={16} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Szukaj po email lub nazwie..."
            className="admin-search-input"
          />
        </div>
        <button className="admin-btn" onClick={handleSearch}>Szukaj</button>
        <span className="admin-count">Łącznie: {total}</span>
      </div>

      <div className="admin-user-list">
        {users.map(u => (
          <div key={u.id} className="admin-user-card">
            <div className="admin-user-header" onClick={() => toggleUserExpand(u.id)}>
              <div className="admin-user-left">
                {expandedUser === u.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <div className="admin-user-info">
                  <span className="admin-user-name">{u.display_name}</span>
                  <span className="admin-user-email">{u.email}</span>
                </div>
              </div>
              <div className="admin-user-right">
                <span className={`admin-role-badge admin-role-badge--${u.role}`}>{u.role}</span>
                <span className="admin-user-meta">{u.project_count} proj / {u.thread_count} wąt / {u.listing_count} ofert</span>
                <span className="admin-user-balance">{formatUSD(u.wallet_balance)}</span>
              </div>
            </div>

            {expandedUser === u.id && (
              <div className="admin-user-details">
                <div className="admin-user-actions">
                  <select
                    value={u.role}
                    onChange={e => handleRoleChange(u.id, e.target.value)}
                    className="admin-select"
                  >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                  <button className="admin-btn admin-btn--danger" onClick={() => handleDeleteUser(u.id, u.email)}>
                    <Trash2 size={14} /> Usuń
                  </button>
                </div>

                <div className="admin-user-stats-row">
                  <span>Wpłacono: {formatUSD(u.total_deposited)}</span>
                  <span>Wydano: {formatUSD(u.total_spent)}</span>
                  <span>Saldo: {formatUSD(u.wallet_balance)}</span>
                  <span>Rejestracja: {new Date(u.created_at).toLocaleDateString()}</span>
                </div>

                <div className="admin-keys-section">
                  <h4><Key size={14} /> Klucze API użytkownika</h4>
                  {userKeys[u.id]?.length === 0 && <p className="admin-muted">Brak kluczy API.</p>}
                  {userKeys[u.id]?.map(k => (
                    <div key={k.id} className="admin-key-row">
                      <span className="admin-key-provider">{k.provider_type}</span>
                      <span className="admin-key-value">
                        {showKeys[k.id] ? k.api_key_encrypted : '••••••••••••'}
                      </span>
                      <button className="admin-icon-btn" onClick={() => toggleKeyVisibility(k.id)} title={showKeys[k.id] ? 'Ukryj' : 'Pokaż'}>
                        {showKeys[k.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function ListingsTab() {
  const [listings, setListings] = useState<any[]>([])

  const fetch = () => { api.adminGetListings().then(setListings).catch(console.error) }
  useEffect(() => { fetch() }, [])

  const handleDelete = async (id: string) => {
    if (!confirm('Usunąć tę ofertę?')) return
    await api.adminDeleteListing(id)
    fetch()
  }

  return (
    <div className="admin-listings">
      <h3>Wszystkie oferty ({listings.length})</h3>
      <div className="admin-table">
        <div className="admin-table-header">
          <span>Dostawca</span>
          <span>Model</span>
          <span>Właściciel</span>
          <span>Input/M</span>
          <span>Output/M</span>
          <span>Zapytania</span>
          <span>Status</span>
          <span></span>
        </div>
        {listings.map(l => (
          <div key={l.id} className="admin-table-row">
            <span>{l.provider_type}</span>
            <span className="admin-mono">{l.model}</span>
            <span title={l.owner_email}>{l.owner_name}</span>
            <span>${Number(l.price_per_1k_input).toFixed(7)}</span>
            <span>${Number(l.price_per_1k_output).toFixed(7)}</span>
            <span>{l.total_requests}</span>
            <span className={`admin-badge ${l.is_active ? 'admin-badge--active' : 'admin-badge--inactive'}`}>
              {l.is_active ? 'Aktywna' : 'Nieaktywna'}
            </span>
            <button className="admin-icon-btn admin-icon-btn--danger" onClick={() => handleDelete(l.id)} title="Usuń ofertę">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function PromoTab() {
  const [codes, setCodes] = useState<any[]>([])
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ code: '', discount_percent: 0, discount_amount: 0, max_uses: 0, valid_until: '' })

  const fetch = () => { api.adminGetPromoCodes().then(setCodes).catch(console.error) }
  useEffect(() => { fetch() }, [])

  const handleCreate = async () => {
    if (!form.code.trim()) return
    await api.adminCreatePromoCode(form)
    setForm({ code: '', discount_percent: 0, discount_amount: 0, max_uses: 0, valid_until: '' })
    setShowForm(false)
    fetch()
  }

  const handleToggleActive = async (id: string, currentActive: number) => {
    await api.adminUpdatePromoCode(id, { is_active: !currentActive })
    fetch()
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Usunąć ten kod promocyjny?')) return
    await api.adminDeletePromoCode(id)
    fetch()
  }

  return (
    <div className="admin-promo">
      <div className="admin-section-header">
        <h3>Kody promocyjne ({codes.length})</h3>
        <button className="admin-btn admin-btn--primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? <><X size={14} /> Anuluj</> : <><Plus size={14} /> Nowy kod</>}
        </button>
      </div>

      {showForm && (
        <div className="admin-form">
          <div className="admin-form-row">
            <label>Kod</label>
            <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="np. WELCOME20" className="admin-input" />
          </div>
          <div className="admin-form-row">
            <label>Zniżka (%)</label>
            <input type="number" value={form.discount_percent} onChange={e => setForm(f => ({ ...f, discount_percent: parseInt(e.target.value) || 0 }))} className="admin-input" />
          </div>
          <div className="admin-form-row">
            <label>Zniżka (centy)</label>
            <input type="number" value={form.discount_amount} onChange={e => setForm(f => ({ ...f, discount_amount: parseInt(e.target.value) || 0 }))} className="admin-input" />
          </div>
          <div className="admin-form-row">
            <label>Max użyć (0 = bez limitu)</label>
            <input type="number" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: parseInt(e.target.value) || 0 }))} className="admin-input" />
          </div>
          <div className="admin-form-row">
            <label>Ważny do</label>
            <input type="date" value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))} className="admin-input" />
          </div>
          <button className="admin-btn admin-btn--primary" onClick={handleCreate}><Check size={14} /> Utwórz</button>
        </div>
      )}

      <div className="admin-table">
        <div className="admin-table-header">
          <span>Kod</span>
          <span>Zniżka</span>
          <span>Użycia</span>
          <span>Ważny do</span>
          <span>Status</span>
          <span></span>
        </div>
        {codes.map(c => (
          <div key={c.id} className="admin-table-row">
            <span className="admin-mono">{c.code}</span>
            <span>
              {c.discount_percent > 0 && `${c.discount_percent}%`}
              {c.discount_percent > 0 && c.discount_amount > 0 && ' + '}
              {c.discount_amount > 0 && `$${Number(c.discount_amount).toFixed(2)}`}
              {!c.discount_percent && !c.discount_amount && '—'}
            </span>
            <span>{c.current_uses}{c.max_uses > 0 ? ` / ${c.max_uses}` : ' / ∞'}</span>
            <span>{c.valid_until ? new Date(c.valid_until).toLocaleDateString() : '—'}</span>
            <span>
              <button className={`admin-badge admin-badge--clickable ${c.is_active ? 'admin-badge--active' : 'admin-badge--inactive'}`} onClick={() => handleToggleActive(c.id, c.is_active)}>
                {c.is_active ? 'Aktywny' : 'Nieaktywny'}
              </button>
            </span>
            <button className="admin-icon-btn admin-icon-btn--danger" onClick={() => handleDelete(c.id)}><Trash2 size={14} /></button>
          </div>
        ))}
        {codes.length === 0 && <p className="admin-muted">Brak kodów promocyjnych.</p>}
      </div>
    </div>
  )
}

const ICON_OPTIONS = [
  { value: 'key', label: 'Key' },
  { value: 'users', label: 'Users' },
  { value: 'crown', label: 'Crown' },
  { value: 'zap', label: 'Zap' },
  { value: 'shield', label: 'Shield' },
  { value: 'star', label: 'Star' },
  { value: 'rocket', label: 'Rocket' },
  { value: 'sparkles', label: 'Sparkles' },
]

function PlansTab() {
  const [plans, setPlans] = useState<any[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: '', price: 0, period: 'monthly', features: '' as string, sort_order: 0,
    description: '', icon: 'key', color: '', is_popular: false, cta_text: '', price_label: '',
  })

  const fetch = () => { api.adminGetPlans().then(setPlans).catch(console.error) }
  useEffect(() => { fetch() }, [])

  const resetForm = () => {
    setForm({ name: '', price: 0, period: 'monthly', features: '', sort_order: 0, description: '', icon: 'key', color: '', is_popular: false, cta_text: '', price_label: '' })
    setEditingId(null)
    setShowForm(false)
  }

  const startEdit = (p: any) => {
    const feats = typeof p.features === 'string' ? JSON.parse(p.features) : p.features
    setForm({
      name: p.name, price: p.price, period: p.period, features: feats.join('\n'), sort_order: p.sort_order,
      description: p.description || '', icon: p.icon || 'key', color: p.color || '',
      is_popular: !!p.is_popular, cta_text: p.cta_text || '', price_label: p.price_label || '',
    })
    setEditingId(p.id)
    setShowForm(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) return
    const data = { ...form, features: form.features.split('\n').filter(Boolean) }
    if (editingId) {
      await api.adminUpdatePlan(editingId, data)
    } else {
      await api.adminCreatePlan(data)
    }
    resetForm()
    fetch()
  }

  const handleToggleActive = async (id: string, currentActive: number) => {
    await api.adminUpdatePlan(id, { is_active: !currentActive })
    fetch()
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Usunąć ten plan?')) return
    await api.adminDeletePlan(id)
    fetch()
  }

  return (
    <div className="admin-plans">
      <div className="admin-section-header">
        <h3>Plany subskrypcji ({plans.length})</h3>
        <button className="admin-btn admin-btn--primary" onClick={() => { if (showForm) resetForm(); else setShowForm(true) }}>
          {showForm ? <><X size={14} /> Anuluj</> : <><Plus size={14} /> Nowy plan</>}
        </button>
      </div>

      {showForm && (
        <div className="admin-form">
          <p className="admin-muted" style={{ marginBottom: 8, fontSize: 12 }}>Dane planu wyswietlane na stronie glownej (Landing Page)</p>
          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>Nazwa planu</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="np. Free, Pro, Premium" className="admin-input" />
            </div>
            <div className="admin-form-row">
              <label>Cena (centy USD)</label>
              <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: parseInt(e.target.value) || 0 }))} className="admin-input" />
            </div>
            <div className="admin-form-row">
              <label>Etykieta ceny (na Landing Page)</label>
              <input value={form.price_label} onChange={e => setForm(f => ({ ...f, price_label: e.target.value }))} placeholder="np. $0, from $0.45, $29" className="admin-input" />
            </div>
            <div className="admin-form-row">
              <label>Okres</label>
              <select value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} className="admin-select">
                <option value="">Brak</option>
                <option value="monthly">Miesięczny</option>
                <option value="yearly">Roczny</option>
                <option value="lifetime">Dożywotni</option>
                <option value="per 1M tokens">Za 1M tokenów</option>
              </select>
            </div>
            <div className="admin-form-row">
              <label>Opis</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Krótki opis planu" className="admin-input" />
            </div>
            <div className="admin-form-row">
              <label>Ikona</label>
              <select value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} className="admin-select">
                {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="admin-form-row">
              <label>Kolor (CSS)</label>
              <input value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} placeholder="np. #a855f7, var(--accent-primary)" className="admin-input" />
            </div>
            <div className="admin-form-row">
              <label>Kolejność</label>
              <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} className="admin-input" />
            </div>
            <div className="admin-form-row">
              <label>Tekst przycisku CTA</label>
              <input value={form.cta_text} onChange={e => setForm(f => ({ ...f, cta_text: e.target.value }))} placeholder="np. Get Started Free, Subscribe" className="admin-input" />
            </div>
            <div className="admin-form-row">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.is_popular} onChange={e => setForm(f => ({ ...f, is_popular: e.target.checked }))} />
                Oznacz jako popularne (wyróżnione)
              </label>
            </div>
          </div>
          <div className="admin-form-row" style={{ marginTop: 8 }}>
            <label>Funkcje (jedna na linię)</label>
            <textarea value={form.features} onChange={e => setForm(f => ({ ...f, features: e.target.value }))} rows={5} className="admin-textarea" placeholder="Nieograniczone projekty&#10;Wszystkie modele AI&#10;Priorytetowe wsparcie" />
          </div>
          <button className="admin-btn admin-btn--primary" onClick={handleSave}>
            <Check size={14} /> {editingId ? 'Zapisz zmiany' : 'Utwórz plan'}
          </button>
        </div>
      )}

      <div className="admin-plans-grid">
        {plans.map(p => {
          const features = typeof p.features === 'string' ? JSON.parse(p.features) : p.features
          return (
            <div key={p.id} className={`admin-plan-card ${!p.is_active ? 'admin-plan-card--inactive' : ''} ${p.is_popular ? 'admin-plan-card--popular' : ''}`}>
              {p.is_popular && <div className="admin-plan-popular-badge">Popular</div>}
              <div className="admin-plan-header">
                <h4>{p.name}</h4>
                <span className="admin-plan-price">
                  {p.price_label || `$${Number(p.price).toFixed(2)}`}
                  {p.period && <small>/{p.period === 'monthly' ? 'mc' : p.period === 'yearly' ? 'rok' : p.period}</small>}
                </span>
              </div>
              {p.description && <p className="admin-plan-desc">{p.description}</p>}
              <ul className="admin-plan-features">
                {features.map((f: string, i: number) => <li key={i}>{f}</li>)}
              </ul>
              <div className="admin-plan-meta">
                <span className="admin-muted">Ikona: {p.icon || 'key'}</span>
                {p.color && <span className="admin-muted">Kolor: <span style={{ color: p.color, fontWeight: 600 }}>{p.color}</span></span>}
                {p.cta_text && <span className="admin-muted">CTA: {p.cta_text}</span>}
              </div>
              <div className="admin-plan-actions">
                <button className={`admin-badge admin-badge--clickable ${p.is_active ? 'admin-badge--active' : 'admin-badge--inactive'}`} onClick={() => handleToggleActive(p.id, p.is_active)}>
                  {p.is_active ? 'Aktywny' : 'Nieaktywny'}
                </button>
                <button className="admin-icon-btn" onClick={() => startEdit(p)} title="Edytuj"><Settings size={14} /></button>
                <button className="admin-icon-btn admin-icon-btn--danger" onClick={() => handleDelete(p.id)} title="Usuń"><Trash2 size={14} /></button>
              </div>
            </div>
          )
        })}
        {plans.length === 0 && <p className="admin-muted">Brak planów subskrypcji.</p>}
      </div>
    </div>
  )
}

function CommissionTab() {
  const [commission, setCommission] = useState({ commission_percent: 15, commission_fixed_cents: 0 })
  const [saved, setSaved] = useState(false)

  useEffect(() => { api.adminGetCommission().then(setCommission).catch(console.error) }, [])

  const handleSave = async () => {
    await api.adminUpdateCommission(commission)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="admin-commission">
      <h3>Ustawienia prowizji</h3>
      <p className="admin-muted">Prowizja naliczana od każdej transakcji dzierżawy API na platformie.</p>

      <div className="admin-form">
        <div className="admin-form-row">
          <label>Prowizja procentowa (%)</label>
          <input
            type="number"
            value={commission.commission_percent}
            onChange={e => setCommission(c => ({ ...c, commission_percent: parseFloat(e.target.value) || 0 }))}
            min={0} max={100}
            className="admin-input"
          />
        </div>
        <div className="admin-form-row">
          <label>Prowizja stała (USD za transakcję)</label>
          <input
            type="number"
            step="0.01"
            value={commission.commission_fixed_cents}
            onChange={e => setCommission(c => ({ ...c, commission_fixed_cents: parseFloat(e.target.value) || 0 }))}
            min={0}
            className="admin-input"
          />
        </div>
        <div className="admin-commission-preview">
          <span>Przykład: transakcja $10.00 → prowizja: ${((10 * commission.commission_percent / 100) + commission.commission_fixed_cents).toFixed(2)}</span>
        </div>
        <button className="admin-btn admin-btn--primary" onClick={handleSave}>
          {saved ? <><Check size={14} /> Zapisano!</> : <><Check size={14} /> Zapisz ustawienia</>}
        </button>
      </div>
    </div>
  )
}
